/**
 * Currency formatting utilities
 */

// Currency symbol mapping
const CURRENCY_SYMBOLS: Record<string, string> = {
  usd: '$',
  eur: '€',
  gbp: '£',
  sek: 'SEK',
  nok: 'NOK',
  dkk: 'DKK',
  jpy: '¥',
  cad: 'C$',
  aud: 'A$',
  chf: 'CHF'
};

// Currencies that show symbol after amount
const SYMBOL_AFTER_AMOUNT = ['sek', 'nok', 'dkk', 'chf'];

/**
 * Format an amount with proper currency symbol/code
 * @param amount - The amount to format (already converted from cents)
 * @param currency - Currency code (e.g., 'usd', 'sek', 'eur')
 * @param showCode - Whether to show currency code instead of symbol
 * @returns Formatted amount string
 */
export function formatCurrency(
  amount: number,
  currency: string = 'usd',
  _showCode: boolean = false
): string {
  const currencyLower = currency.toLowerCase();
  const symbol = CURRENCY_SYMBOLS[currencyLower] || currencyLower.toUpperCase();

  // For currencies that show symbol after amount
  if (SYMBOL_AFTER_AMOUNT.includes(currencyLower)) {
    return `${amount} ${symbol}`;
  }

  // For currencies that show symbol before amount
  return `${symbol}${amount}`;
}

/**
 * Format subscription amount with billing cycle
 * @param amount - The amount (already converted from cents)
 * @param currency - Currency code
 * @param billingCycle - 'month' or 'year'
 * @param showCode - Whether to show currency code instead of symbol
 * @param locale - Locale for translations (default: 'sv' for Swedish)
 * @returns Formatted subscription string (e.g., "79 SEK/månad", "$79/mo")
 */
export function formatSubscriptionAmount(
  amount: number,
  currency: string = 'usd',
  billingCycle: string = 'month',
  _showCode: boolean = false,
  locale: string = 'sv'
): string {
  const formattedAmount = formatCurrency(amount, currency, _showCode);

  // Swedish translations for billing cycles
  let cycle: string;
  if (locale === 'sv') {
    cycle = billingCycle === 'month' ? '/månad' : '/år';
  } else {
    // Default to English
    cycle = billingCycle === 'month' ? '/mo' : '/yr';
  }

  return `${formattedAmount}${cycle}`;
}
